import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { Mail, CheckCircle2, AlertTriangle, Clock, Send } from 'lucide-react';

const STATUS_ICON = {
  sent: <CheckCircle2 size={14} className="mr-1.5" />,
  queued: <Clock size={14} className="mr-1.5" />,
  failed: <AlertTriangle size={14} className="mr-1.5" />,
};

export default function AdminEmails() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ resend_configured: false, sender_email: '' });
  const [form, setForm] = useState({ to: '', subject: 'Heritage Apparel test email', body: '<p>Hello from Heritage.</p>' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    const { data } = await api.get('/admin/email-log');
    setRows(data.emails || []);
    setMeta({ resend_configured: !!data.resend_configured, sender_email: data.sender_email });
  };
  useEffect(() => { load(); }, []);

  const sendTest = async (e) => {
    e.preventDefault();
    setSending(true); setResult(null);
    try {
      const { data } = await api.post('/admin/send-test-email', form);
      setResult(data.entry);
      load();
    } catch (err) {
      setResult({ status: 'failed', error: err?.response?.data?.detail || 'Failed' });
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <Panel className="p-6">
        <div className="flex items-center gap-2 h-eyebrow"><Mail size={14} /> Email delivery</div>
        <div className="mt-2 flex flex-wrap items-center gap-4">
          <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>
            Provider status:
            <span className={`ml-2 h-chip ${meta.resend_configured ? '' : ''}`}
                  style={{ background: meta.resend_configured ? 'rgba(143,163,75,0.18)' : 'rgba(75,24,56,0.08)',
                           borderColor: meta.resend_configured ? 'rgba(143,163,75,0.4)' : 'rgba(75,24,56,0.2)',
                           color: meta.resend_configured ? '#5B6E2C' : 'var(--heritage-plum)' }}>
              {meta.resend_configured ? 'Resend live' : 'Not configured (queued only)'}
            </span>
          </div>
          <div className="text-sm opacity-80" style={{ color: 'var(--heritage-plum)' }}>Sender: {meta.sender_email}</div>
        </div>
        {!meta.resend_configured && (
          <p className="text-xs opacity-80 mt-2" style={{ color: 'var(--heritage-plum)' }}>
            Add <code>RESEND_API_KEY</code> and <code>SENDER_EMAIL</code> to <code>/app/backend/.env</code> and restart the backend to enable live sending. Until then, every triggered email is stored below in the queue.
          </p>
        )}
      </Panel>

      <Panel className="p-6">
        <div className="h-eyebrow mb-3">Send a test email</div>
        <form onSubmit={sendTest} className="grid md:grid-cols-3 gap-3">
          <input className="h-input md:col-span-1" required type="email" placeholder="to@example.com" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="test-email-to" />
          <input className="h-input md:col-span-2" required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <textarea className="h-input md:col-span-3" rows={3} required placeholder="HTML body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <button className="h-btn md:col-span-3" disabled={sending} data-testid="test-email-send">
            <Send size={14} className="mr-2" /> {sending ? 'Sending...' : 'Send test'}
          </button>
        </form>
        {result && (
          <div className="mt-3 text-sm" style={{ color: result.status === 'failed' ? '#B91C1C' : 'var(--heritage-plum)' }}>
            Result: <strong>{result.status}</strong> {result.provider_id && `(id ${result.provider_id})`} {result.error && `: ${result.error}`}
          </div>
        )}
      </Panel>

      <Panel className="p-6">
        <div className="h-eyebrow mb-3">Recent email log</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left h-eyebrow"><th className="py-2">When</th><th>To</th><th>Category</th><th>Subject</th><th>Status</th></tr></thead>
            <tbody style={{ color: 'var(--heritage-plum)' }}>
              {rows.map(r => (
                <tr key={r.id} className="border-t" style={{ borderColor: 'rgba(75,24,56,0.1)' }}>
                  <td className="py-2 whitespace-nowrap">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="whitespace-nowrap">{r.to}</td>
                  <td className="capitalize">{r.category}</td>
                  <td className="h-serif">{r.subject}</td>
                  <td className="capitalize">
                    <span className="h-chip inline-flex items-center"
                          style={{
                            background: r.status === 'sent' ? 'rgba(143,163,75,0.15)' : r.status === 'failed' ? 'rgba(185,28,28,0.12)' : 'rgba(75,24,56,0.08)',
                            borderColor: r.status === 'sent' ? 'rgba(143,163,75,0.4)' : r.status === 'failed' ? 'rgba(185,28,28,0.4)' : 'rgba(75,24,56,0.2)',
                            color: r.status === 'sent' ? '#5B6E2C' : r.status === 'failed' ? '#B91C1C' : 'var(--heritage-plum)',
                          }}>
                      {STATUS_ICON[r.status] || null}{r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="text-sm opacity-70 py-3" style={{ color: 'var(--heritage-plum)' }}>No emails logged yet.</div>}
        </div>
      </Panel>
    </div>
  );
}
